import { useCallback, useEffect, useState } from "react";
import { Gauge } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "../../lib/api";
import { StatusBadge } from "../StatusBadge";
import { Button } from "../ui/button";
import { NoManuscript } from "./NoManuscript";

const pct = (s) => Math.round((s / 3) * 100);
const tone = (n) => (n >= 67 ? "PASS" : n >= 40 ? "WARNING" : "ERROR");

function Meter({ label, score, testId }) {
  const n = pct(score);
  return (
    <div className="min-w-[110px]" data-testid={testId}>
      <div className="flex justify-between text-[11px] text-slate-400"><span>{label}</span><span className="font-mono">{n}</span></div>
      <div className="h-1.5 bg-slate-800 rounded mt-1"><div className={`h-full rounded ${n >= 67 ? "bg-emerald-500" : n >= 40 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${n}%` }} /></div>
    </div>
  );
}

function Row({ r }) {
  if (r.error) return <div className="p-4 text-sm text-rose-300" data-testid={`insight-${r.chapter_id}`}>{r.title}: {r.error}</div>;
  const quality = (r.readability + r.pacing) / 2;
  const conflict = r.continuity_conflict;
  return (
    <div className="p-4 flex flex-wrap gap-4 items-center justify-between" data-testid={`insight-${r.chapter_id}`}>
      <div className="min-w-[180px]"><p className="text-sm text-slate-200">{r.title}</p>
        <p className="text-[11px] text-slate-500">Reads like {r.genre} ({Math.round(r.genre_confidence * 100)}%){r.excerpt_only ? " · first 6,000 characters" : ""}</p></div>
      <div className="flex gap-4 items-center">
        <StatusBadge status={tone(pct(quality))} label={`quality ${pct(quality)}`} testId={`insight-quality-${r.chapter_id}`} />
        <Meter label="Readability" score={r.readability} testId={`insight-readability-${r.chapter_id}`} />
        <Meter label="Pacing" score={r.pacing} testId={`insight-pacing-${r.chapter_id}`} />
      </div>
      <div className="flex gap-1 flex-wrap" data-testid={`insight-tones-${r.chapter_id}`}>{r.tones.map((t) => <span key={t.tone} className="text-[11px] px-2 py-0.5 rounded-full border border-slate-700 text-slate-300">{t.tone}</span>)}{!r.tones.length && <span className="text-[11px] text-slate-500">no dominant tone</span>}</div>
      <div data-testid={`insight-conflict-${r.chapter_id}`}>{conflict == null ? <StatusBadge status="NOT VERIFIED" label="no confirmed canon" /> : conflict >= 0.5 ? <StatusBadge status="ERROR" label={`possible canon conflict ${Math.round(conflict * 100)}%`} /> : <StatusBadge status="PASS" label="consistent with canon" />}</div>
    </div>
  );
}

export default function InsightsTab({ book, version }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/books/${book.id}/insights`).then((r) => setRows(r.data.results)), [book.id]);
  useEffect(() => { load(); }, [load, version?.id]);
  if (!version) return <NoManuscript />;
  const run = async () => { setBusy(true); try { const { data } = await api.post(`/books/${book.id}/insights`, {}); toast.success(`Analysed ${data.results.length} sections against ${data.canon_facts_checked} canon facts`); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const flagged = rows.filter((r) => r.continuity_conflict >= 0.5).length;
  return (
    <div className="space-y-4">
      <div className="panel p-5 flex flex-wrap gap-4 items-center justify-between" data-testid="insights-header">
        <div><p className="eyebrow">Fast chapter checks · v{version.number}</p><p className="text-sm text-slate-400 max-w-2xl">Quality (readability + pacing), genre and mood tags, and a yes/no flag when a chapter may contradict confirmed facts in your Continuity Manager. These are quick signals, not edits — nothing in your manuscript changes.</p></div>
        <Button onClick={run} disabled={busy} className="bg-amber-600 hover:bg-amber-500" data-testid="run-insights-button"><Gauge size={14} className="mr-1" />{busy ? "Analysing chapters…" : rows.length ? "Re-run checks" : "Run chapter checks"}</Button>
      </div>
      {flagged > 0 && <p className="text-sm text-rose-300" data-testid="insights-conflict-summary">{flagged} section(s) may conflict with confirmed canon — open the Continuity Manager to run a detailed check.</p>}
      <div className="panel divide-y divide-slate-800" data-testid="insights-list">
        {rows.length === 0 && <p className="p-4 text-sm text-slate-500">No checks yet for this version.</p>}
        {rows.map((r) => <Row key={r.chapter_id} r={r} />)}
      </div>
      {rows[0] && <p className="text-[11px] text-slate-500">Last run {fmtDate(rows[0].created_at)}. Long chapters are judged on their first 6,000 characters.</p>}
    </div>
  );
}
